import { BodyText } from "@/components/ui";
import type { FormularyCopay } from "./network/useFormularyLookup";

export const copayAmountDisplay = (
  copay: FormularyCopay | null | undefined,
): string | null => {
  if (!copay) return null;
  const parts: string[] = [];
  if (copay.flatCopayAmount != null)
    parts.push(`$${copay.flatCopayAmount.toFixed(2)}`);
  if (copay.percentCopayAmount != null)
    parts.push(`${(copay.percentCopayAmount * 100).toFixed(0)}%`);
  return parts.length > 0 ? parts.join(" + ") : null;
};

export const copayMinMaxLabel = (copay: FormularyCopay): string | null =>
  copay.minimumCopayAmount != null || copay.maximumCopayAmount != null
    ? `(min $${copay.minimumCopayAmount ?? "—"} / max $${copay.maximumCopayAmount ?? "—"})`
    : null;

const formatTier = (copay: FormularyCopay): string => {
  if (copay.copayTier == null) return "—";
  return copay.maximumCopayTier != null
    ? `${copay.copayTier} (${copay.maximumCopayTier})`
    : `${copay.copayTier}`;
};

const CopayRow = ({ copay }: { copay: FormularyCopay }) => {
  const copayDisplay = copayAmountDisplay(copay) ?? "—";
  const minMax = copayMinMaxLabel(copay);
  const tier = formatTier(copay);

  const oop =
    copay.outOfPocketMinimum != null || copay.outOfPocketMaximum != null
      ? `$${copay.outOfPocketMinimum ?? "—"} / $${copay.outOfPocketMaximum ?? "—"}`
      : "—";

  return (
    <div className="grid grid-cols-6 gap-x-2 text-sm py-1.5 px-3 border-b last:border-0">
      <span className="text-tertiary">{copay.pharmacyType ?? "All"}</span>
      <span>{copay.daysSupply != null ? `${copay.daysSupply}d` : "—"}</span>
      <span>
        {copayDisplay}
        {minMax && <span className="text-xs text-tertiary ml-1">{minMax}</span>}
      </span>
      <span>{copay.flatCopayAmountFirst ? "Yes" : "-"}</span>
      <span>{tier}</span>
      <span>{oop}</span>
    </div>
  );
};

interface CopayTableProps {
  copays: FormularyCopay[];
}

export const CopayTable = ({ copays }: CopayTableProps) => {
  if (copays.length === 0) return null;

  return (
    <div className="flex flex-col gap-2">
      <BodyText size="sm" className="font-medium">
        Copays
      </BodyText>
      <div className="rounded-md border overflow-hidden">
        <div className="grid grid-cols-6 gap-x-2 text-xs text-tertiary py-1.5 px-3 border-b">
          <span>Pharmacy</span>
          <span>Days Supply</span>
          <span>Copay</span>
          <span>Flat Copay First</span>
          <span>Tier (Max Tier)</span>
          <span>OOP (min / max)</span>
        </div>
        {copays.map((copay, i) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: copay rows have no stable key
          <CopayRow key={i} copay={copay} />
        ))}
      </div>
    </div>
  );
};
